import { AlertColor, Box, Button, Chip, FormControl, InputLabel, MenuItem, Select, Stack, Typography } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import strings from '../../../../common/Translation/Translate';
import Notification from '../../../../components/Notification';
import Upload from '../../../../components/Upload/Upload';
import { FormDataModel } from '../../../../components/Form';
import axiosInstance from '../../../../utils/axios';
import Config from '../../../../utils/Config';
import { PageProps } from '../../../../utils/constants/data';
import { privileges } from '../../../../utils/constants/privileges';
import { URLS } from '../../../../utils/constants/urls';
import { Privilege, languages } from '../../../../utils/redux/reducer/authentication-slice';
import { filterDataforApi, pageNo, rowCount } from 'utils/redux/reducer/filter-slice';
import { createDropdownData, getListData } from '../../../../utils/validators/HelperFunctions';
import ListPage from '../../CommonPage';
import MasterDataEntityDrawer from '../../CommonPage/MasterDataEntityDrawer';
import { lastUpdatedTableColumn } from '../../CommonPage/lastUpdatedColumn';
import { usageTableColumn } from '../../CommonPage/usageColumn';
import { AddEditAddOnsForm } from './AddEditAddOns/AddEditAddOnsForm';
import { BulkEditAddOnsForm } from './AddEditAddOns/BulkEditAddOnsForm';

const priceUnitLabel = (addOnType?: string) =>
  addOnType === 'PER_GUEST' ? 'person' : addOnType === 'PER_QUANTITY' ? 'unit' : '';

const formatPriceWithUnit = (row: any) => {
  const amount = row.formattedPrice ?? row.price ?? '—';
  const unit = priceUnitLabel(row.addOnType);
  return unit ? `${amount} / ${unit}` : amount;
};

const stripFilterKey = (filter: unknown, key: string) => {
  const base = Array.isArray(filter)
    ? filter.filter((item: unknown) => typeof item !== 'string' || !item.startsWith(`${key}:`))
    : [];

  const cleaned: string[] = [];
  base.forEach((item: string) => {
    if (item === 'and') {
      if (cleaned.length === 0 || cleaned[cleaned.length - 1] === 'and') return;
      cleaned.push(item);
      return;
    }
    cleaned.push(item);
  });
  if (cleaned.length && cleaned[cleaned.length - 1] === 'and') cleaned.pop();
  return cleaned;
};

const withEqFilter = (filter: unknown, key: string, value: string) => {
  const cleaned = stripFilterKey(filter, key);
  if (!value) return cleaned;
  if (cleaned.length) cleaned.push('and');
  cleaned.push(`${key}:eq:${value}`);
  return cleaned;
};

const AddOnsList: FC = () => {
  const index = useSelector(pageNo);
  const filter = useSelector(filterDataforApi);
  const pageSize = useSelector(rowCount);
  const languageList = useSelector(languages) as { text: string; value: string }[];
  const userPrivileges = useSelector(Privilege) as string[];
  const [searchParams, setSearchParams] = useSearchParams();

  const [addOnCategories, setAddOnCategories] = useState<any[]>([]);
  const [taxGroups, setTaxGroups] = useState<any[]>([]);
  const [pageResponse, setPageResponse] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkIds, setBulkIds] = useState<number[]>([]);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formValues, setFormValues] = useState<FormDataModel>({});
  const [bulkValues, setBulkValues] = useState<FormDataModel>({});
  const [selectedAddOnCategory, setSelectedAddOnCategory] = useState(
    () => searchParams.get('addOnCategory') || '',
  );
  const [addOnImage, setAddOnImage] = useState<any[]>([]);
  const [deletedImage, setDeletedImage] = useState<any[]>([]);
  const [message, setMessage] = useState<{ display: boolean; severity: AlertColor | null; message: string }>({
    display: false,
    severity: null,
    message: '',
  });

  const formModel = useMemo(() => {
    const initial = AddEditAddOnsForm(strings, addOnCategories);
    const fields = [...initial];
    (languageList || []).forEach(language => {
      const nameObject = { ...initial[0] };
      nameObject.field = `name_${language.value}`;
      nameObject.label = `${nameObject.label} (${language.text})`;
      nameObject.responsive = { xs: 12 };
      fields.unshift(nameObject);

      const descriptionObject = { ...initial[1] };
      descriptionObject.field = `description_${language.value}`;
      descriptionObject.label = `${descriptionObject.label} (${language.text})`;
      descriptionObject.responsive = { xs: 12 };
      fields.push(descriptionObject);
    });
    return fields
      .filter(item => item.field !== 'name' && item.field !== 'description' && item.type !== 'section')
      .map(item => {
        if (item.field === 'taxGroup') {
          return {
            ...item,
            options: createDropdownData(taxGroups, ['id', 'name']) as any,
            responsive: { xs: 12, sm: 6 },
          };
        }
        return {
          ...item,
          responsive:
            item.field?.startsWith('name_') || item.field?.startsWith('description_')
              ? { xs: 12 }
              : { xs: 12, sm: 6 },
        };
      });
  }, [addOnCategories, languageList, taxGroups]);

  const bulkFormModel = useMemo(
    () => BulkEditAddOnsForm(strings, addOnCategories),
    [addOnCategories],
  );

  const getList = useCallback(
    (filterData: PageProps) => {
      const body = filterData
        ? {
            ...filterData,
            filter: withEqFilter(filterData.filter, 'addOnCategory', selectedAddOnCategory),
          }
        : {
            page: index + 1,
            size: pageSize,
            filter: withEqFilter(filter ?? [], 'addOnCategory', selectedAddOnCategory),
            sort: ['createdAt:desc'],
          };
      getListData(setLoading, body, URLS.addOns, setPageResponse);
    },
    [index, pageSize, filter, selectedAddOnCategory],
  );

  useEffect(() => {
    getList(null as any);
  }, [getList]);

  useEffect(() => {
    const categoryFromUrl = searchParams.get('addOnCategory') || '';
    setSelectedAddOnCategory(prev => (prev === categoryFromUrl ? prev : categoryFromUrl));
  }, [searchParams]);

  const handleCategoryFilterChange = (value: string) => {
    setSelectedAddOnCategory(value);
    const nextParams = new URLSearchParams(searchParams);
    if (value) nextParams.set('addOnCategory', value);
    else nextParams.delete('addOnCategory');
    setSearchParams(nextParams, { replace: true });
  };

  useEffect(() => {
    (async () => {
      try {
        const [{ data: categories }, { data: groups }] = await Promise.all([
          axiosInstance.get(URLS.getAddOnCategoriesDropdown),
          axiosInstance.get(URLS.taxGroupDropdown),
        ]);
        setAddOnCategories(
          (categories || []).map((item: any) => ({ value: item.id, label: item.name })),
        );
        setTaxGroups(groups || []);
      } catch {
        // ignore dropdown load failures
      }
    })();
  }, []);

  const closeNotification = (value: boolean) => setMessage(prev => ({ ...prev, display: value }));

  const handleCatch = (error: any) => {
    setMessage({
      display: true,
      severity: 'error',
      message: error?.response?.data?.error_description ?? error?.response?.data?.message ?? error?.message,
    });
  };

  const getFileDetails = (value: any, _field: string, _error?: any, isDeleted?: any) => {
    if (Array.isArray(isDeleted) && isDeleted[0]?.name) {
      setDeletedImage(prev => [...prev, isDeleted[0].name]);
    }
    setAddOnImage(Array.isArray(value) ? value : []);
  };

  const openDrawer = useCallback(
    async (recordId: number | null) => {
      setEditingId(recordId);
      setDeletedImage([]);
      if (recordId) {
        try {
          setDrawerLoading(true);
          const { data } = await axiosInstance.get(URLS.get_edit_delete_addOnsById(recordId));
          const container: FormDataModel = {
            addOnType: data.addOnType?.value ?? data.addOnType ?? '',
            addOnCategory:
              data.addOnCategory?.value ?? data.addOnCategory?.id ?? data.addOnCategory ?? '',
            maxCount: data.maxCount ?? '',
            price: data.price ?? '',
            hsnCode: data.hsnCode ?? '',
            taxGroup: data.taxGroup ?? '',
          };
          (languageList || []).forEach(language => {
            container[`name_${language.value}`] = data?.localizedAttributes?.[language.value]?.name ?? '';
            container[`description_${language.value}`] =
              data?.localizedAttributes?.[language.value]?.description ?? '';
          });
          const images: any[] = [];
          if (data.addOnImage) {
            images.push({
              preview: Config.BaseURL + data.addOnImage,
              name: data.fileName,
              id: data.id,
            });
          }
          setAddOnImage(images);
          setFormValues(container);
        } catch (error: any) {
          handleCatch(error);
          return;
        } finally {
          setDrawerLoading(false);
        }
      } else {
        const container: FormDataModel = {
          addOnType: '',
          addOnCategory: '',
          maxCount: '',
          price: '',
          hsnCode: '',
          taxGroup: '',
        };
        (languageList || []).forEach(language => {
          container[`name_${language.value}`] = '';
          container[`description_${language.value}`] = '';
        });
        setAddOnImage([]);
        setFormValues(container);
      }
      setDrawerOpen(true);
    },
    [languageList],
  );

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingId(null);
    setFormValues({});
    setAddOnImage([]);
    setDeletedImage([]);
  };

  const uploadImageIfNeeded = async (addOnId: number) => {
    if (!addOnImage?.length && !deletedImage?.length) return;
    const formData = new FormData();
    const file = addOnImage?.[0];
    if (file && file instanceof File) {
      formData.append('addOnImage', file);
    }
    if (deletedImage?.[0]) {
      formData.append('deletedFile', deletedImage[0]);
    }
    if ([...formData.keys()].length === 0) return;
    await axiosInstance.put(URLS.addon_image(addOnId), formData);
  };

  const handleSave = async (body: FormDataModel) => {
    try {
      setSaving(true);
      const localizedAttributes = (languageList || []).map(item => ({
        language: item.value,
        name: body[`name_${item.value}`],
        description: body[`description_${item.value}`],
      }));
      const payload = {
        addOnType: body.addOnType,
        addOnCategory: body.addOnCategory,
        maxCount: body.maxCount,
        price: body.price,
        hsnCode: body.hsnCode,
        taxGroupId: (body.taxGroup as any)?.value ?? body.taxGroup ?? null,
        localizedAttributes,
      };
      let savedId = editingId;
      if (editingId) {
        await axiosInstance.put(URLS.get_edit_delete_addOnsById(editingId), payload);
      } else {
        const { data } = await axiosInstance.post(URLS.add_addOns, payload);
        savedId = data?.id ?? data;
      }
      if (savedId) {
        await uploadImageIfNeeded(Number(savedId));
      }
      setMessage({
        display: true,
        severity: 'success',
        message: editingId ? strings.common_update_message : strings.common_save_message,
      });
      closeDrawer();
      getList(null as any);
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editingId) return;
    try {
      setSaving(true);
      await axiosInstance.delete(URLS.get_edit_delete_addOnsById(editingId));
      setMessage({ display: true, severity: 'success', message: strings.common_delete_message });
      closeDrawer();
      getList(null as any);
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setSaving(false);
    }
  };

  const openBulkEdit = (selectedIds: number[]) => {
    setBulkIds(selectedIds);
    setBulkValues({});
    setBulkOpen(true);
  };

  const handleBulkSave = async (body: FormDataModel) => {
    try {
      setSaving(true);
      const payload: Record<string, any> = { ids: bulkIds };
      if (body.addOnType) payload.addOnType = body.addOnType;
      if (body.addOnCategory) payload.addOnCategory = body.addOnCategory;
      if (body.price !== '' && body.price != null) payload.price = Number(body.price);
      if (body.maxCount !== '' && body.maxCount != null) payload.maxCount = Number(body.maxCount);
      if (body.hsnCode !== '' && body.hsnCode != null) payload.hsnCode = body.hsnCode;
      if ((body.taxGroup as any)?.value != null) payload.taxGroupId = (body.taxGroup as any).value;

      await axiosInstance.patch(URLS.bulkUpdateAddOns, payload);
      setMessage({
        display: true,
        severity: 'success',
        message: strings.common_update_message,
      });
      setBulkOpen(false);
      setBulkIds([]);
      getList(null as any);
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setSaving(false);
    }
  };

  const pageConfig = useMemo(
    () => ({
      title: strings.addOn_title_list,
      subtitle: strings.addOn_list_subtitle,
      searchField: 'addOnName',
      searchPlaceholder: 'Search add-ons',
      AddButtonText: strings.addOn_button_add,
      listApi: URLS.addOns,
      deleteApi: URLS.get_edit_delete_addOnsById,
      addPrivilege: privileges.add_addOn,
      editPrivilege: privileges.edit_addOn,
      deletePrivilege: privileges.delete_addOn,
      importPrivilege: privileges.import_addOn,
      importApi: URLS.import_addOn,
      downloadImportTemplate: URLS.download_import_addons_template,
      customAdd: () => openDrawer(null),
      customEdit: (id: number) => openDrawer(id),
      customComponent: () => (
        <FormControl size="small" sx={{ minWidth: 180, mr: 1 }}>
          <InputLabel sx={{ fontSize: 12 }}>Category</InputLabel>
          <Select
            label="Category"
            value={selectedAddOnCategory}
            onChange={event => handleCategoryFilterChange(String(event.target.value))}
            sx={{
              height: 36,
              fontSize: 13,
              bgcolor: '#fff',
              '& .MuiSelect-select': { py: 0.75 },
            }}>
            <MenuItem value="">All categories</MenuItem>
            {addOnCategories.map(option => (
              <MenuItem key={String(option.value)} value={String(option.value)} sx={{ fontSize: 13 }}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ),
      enableRowSelection: userPrivileges?.includes(privileges.edit_addOn),
      selectionToolbar: (selectedIds: number[], clearSelection: () => void) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1,
            px: 1.5,
            py: 1,
            borderRadius: 2,
            border: theme => `1px solid ${theme.palette.divider}`,
            bgcolor: 'action.hover',
          }}>
          <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
            Updating {selectedIds.length} selected add-on{selectedIds.length === 1 ? '' : 's'}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="contained" onClick={() => openBulkEdit(selectedIds)}>
              Bulk edit
            </Button>
            <Button size="small" color="inherit" onClick={clearSelection}>
              Clear selection
            </Button>
          </Stack>
        </Box>
      ),
      tableColumn: [
        {
          field: 'imageUrl',
          headerName: '',
          flex: 0.6,
          image: true,
          imageInitialsField: 'addOnName',
        },
        {
          field: 'addOnName',
          headerName: 'Add-on',
          flex: 2,
          sortable: true,
          localField: 'localizedAttributes.name',
        },
        {
          field: 'addOnCategoryName',
          headerName: 'Category',
          flex: 1.4,
          sortable: true,
          localField: 'addOnCategory',
          renderCell: (value: any) =>
            value ? <Chip size="small" label={value} sx={{ fontWeight: 600 }} /> : '—',
        },
        {
          field: 'formattedPrice',
          headerName: 'Price',
          flex: 1.4,
          sortable: true,
          localField: 'price',
          renderCell: (_value: any, row: any) => formatPriceWithUnit(row),
        },
        {
          field: 'maxCount',
          headerName: 'Maximum',
          flex: 1,
          sortable: true,
        },
        usageTableColumn(row => [
          { count: row.venueConfigurationCount, singular: 'Config', plural: 'Configs' },
          { count: row.bookingCount, singular: 'Booking', plural: 'Bookings' },
        ]),
        lastUpdatedTableColumn(),
      ],
    }),
    [addOnCategories, openDrawer, selectedAddOnCategory, userPrivileges],
  );

  return (
    <>
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
        />
      )}
      <ListPage
        isLoading={loading}
        isWhiteSpace={false}
        pageConfig={pageConfig}
        pageResponse={pageResponse}
        getList={getList}
      />
      <MasterDataEntityDrawer
        open={drawerOpen}
        title={editingId ? strings.addOn_title_edit : strings.addOn_title_add}
        isEdit={Boolean(editingId)}
        formModel={formModel}
        formValues={formValues}
        loading={drawerLoading}
        saving={saving}
        canDelete={userPrivileges?.includes(privileges.delete_addOn)}
        width="50%"
        onClose={closeDrawer}
        onSave={handleSave}
        onDelete={handleDelete}
        onInvalid={msg => setMessage({ display: true, severity: 'error', message: msg })}>
        <Box sx={{ mt: 2 }}>
          <Typography sx={{ mb: 1, fontWeight: 600, fontSize: '0.875rem' }}>Add-on image</Typography>
          <Upload
            field="addOnImage"
            textChange={getFileDetails}
            inputValue={addOnImage}
            accept="image/*"
          />
        </Box>
      </MasterDataEntityDrawer>
      <MasterDataEntityDrawer
        open={bulkOpen}
        title={`Bulk edit (${bulkIds.length})`}
        isEdit={false}
        formModel={bulkFormModel.map(field =>
          field.field === 'taxGroup'
            ? { ...field, options: createDropdownData(taxGroups, ['id', 'name']) as any }
            : field,
        )}
        formValues={bulkValues}
        saving={saving}
        skipValidation
        width="50%"
        saveLabel="Apply to selected"
        onClose={() => {
          setBulkOpen(false);
          setBulkIds([]);
        }}
        onSave={handleBulkSave}
        onInvalid={msg => setMessage({ display: true, severity: 'error', message: msg })}
      />
    </>
  );
};

export default AddOnsList;
